import type { ReactiveControllerHost } from 'lit';
import { CustomError } from '../../../../_ui-core_/base/package/patterns/package/CError';
import { sanitizePathSegment } from '../../../../_ui-core_/base/package/util/url';
import App from '../../../../app';
import BaseController, { type EventAction, type IGameController } from '../base-controller';

type OryxEvent = {
  wpgaction: string;
};

export default class OryxGameController extends BaseController implements IGameController {
  constructor(host: ReactiveControllerHost) {
    super(host, 'oryx');
  }

  public async getLaunchUrl(forReal: boolean, gameId: string): Promise<string> {
    try {
      const info = await App.instance.casino.getGameLaunchInfo(this.provider, gameId, forReal);

      // temp solution to enable launch with responses returned from provider-integration
      if (info.launchConfig.launchUrl) {
        return info.launchConfig.launchUrl;
      }

      if (!info.launchConfig.serverUrl || !info.launchConfig.operatorId) {
        return Promise.reject(new CustomError('Missing fields in info.launchConfig.', { code: 404 }));
      }

      const safeGameId = sanitizePathSegment(gameId);
      if (!safeGameId) {
        return Promise.reject(new CustomError('Invalid gameId.', { code: 400 }));
      }

      const searchParams = new URLSearchParams({
        languageCode: OryxGameController.formatLanguage(info.language),
        playMode: forReal ? 'REAL' : 'FUN',
        ...(info.playForReal ? { token: info.playForReal.token } : {}),
      });

      return `${info.launchConfig.serverUrl}/${info.launchConfig.operatorId}/games/${safeGameId}/open?${searchParams}`;
    } catch (error: any) {
      return Promise.reject(
        new CustomError(`Failed to get game launch info: ${error.message}`, {
          code: error.code || 500,
          type: error.type,
        }),
      );
    }
  }

  public getLaunchUrlIOs(forReal: boolean, gameId: string, gameBaseUrl: string): Promise<string> {
    window.$app.logger.warn('TODO getLaunchUrlIOs: NATIVE GAME LAUNCH NOT IMPLEMENTED', gameId, gameBaseUrl);
    return this.getLaunchUrl(forReal, gameId); // TODO
  }

  public handleMessageEvent(data: string | object): EventAction | null {
    window.$app.logger.log('message', data);
    const { wpgaction } = data as OryxEvent;

    const markActivityEvents = [
      'betAmountChange',
      'gamePaused',
      'gameResumed',
      'gameSpinStart',
      'openGameHistory',
      'soundOff',
      'soundOn',
    ];

    if (markActivityEvents.includes(wpgaction)) {
      return this.markActivity();
    }

    switch (wpgaction) {
      case 'gameSpinEnd':
        return this.markActivityAndSpinSuccess();
      case 'close':
      case 'errorOutOfMoney':
        return { action: 'error', error: 'errorOutOfMoney' };
      case 'cashier':
        return this.closeGame();
    }

    return null;
  }

  public handleAdditionalEventListeners(_iframeUrl: string, _iframeEl: HTMLIFrameElement) {
    return;
  }

  private static formatLanguage(code: string): string {
    switch (code) {
      case 'en':
        return 'ENG';
      default:
        return 'GER';
    }
  }
}
